import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { Color } from '@intelliarts/react-components';

import { DESTRUCTIVE_COLOR } from '../../../utils';

/** Mirrors hrm-service `com.intelliarts.hrm.status.model.EmployeeStatus`. */
export enum EmployeeStatus {
  ACTIVE = 'ACTIVE',
  BENCH = 'BENCH',
  MILITARY_LEAVE = 'MILITARY_LEAVE',
  MATERNITY_LEAVE = 'MATERNITY_LEAVE',
  SABBATICAL = 'SABBATICAL',
  COMPASSIONATE_LEAVE = 'COMPASSIONATE_LEAVE',
  PARENTAL_DAYS = 'PARENTAL_DAYS',
  REHIRED = 'REHIRED',
  INACTIVE = 'INACTIVE',
}

export const ALL_EMPLOYEE_STATUSES = Object.values(EmployeeStatus);

/** Leave-type statuses for the Leaves page filter. */
export const LEAVE_EMPLOYEE_STATUSES: EmployeeStatus[] = [
  EmployeeStatus.MILITARY_LEAVE,
  EmployeeStatus.MATERNITY_LEAVE,
  EmployeeStatus.SABBATICAL,
];

/**
 * Brandbook status-badge colours (platform-web Badge family):
 * success = Dark Blue, secondary = teal tint, destructive = Deep Orange, outline = neutral.
 */
const STATUS_CHIP_SX = {
  active: {
    backgroundColor: alpha(Color.DARK_BLUE, 0.1),
    color: Color.DARK_BLUE,
    border: `1px solid ${Color.DARK_BLUE}`,
  },
  bench: {
    backgroundColor: alpha(Color.LIGHT_BLUE, 0.2),
    color: Color.DARK_BLUE,
    border: '1px solid transparent',
  },
  inactive: {
    backgroundColor: alpha(DESTRUCTIVE_COLOR, 0.1),
    color: DESTRUCTIVE_COLOR,
    border: `1px solid ${DESTRUCTIVE_COLOR}`,
  },
  leave: {
    backgroundColor: 'transparent',
    color: 'text.primary',
    border: '1px solid',
    borderColor: 'divider',
  },
} as const satisfies Record<string, SxProps<Theme>>;

/** Active → success; Bench → secondary; Inactive → destructive; leaves / rest → outline. */
export const employeeStatusChipSx = (status: EmployeeStatus): SxProps<Theme> => {
  if (status === EmployeeStatus.ACTIVE || status === EmployeeStatus.REHIRED) {
    return STATUS_CHIP_SX.active;
  }
  if (status === EmployeeStatus.BENCH) {
    return STATUS_CHIP_SX.bench;
  }
  if (status === EmployeeStatus.INACTIVE) {
    return STATUS_CHIP_SX.inactive;
  }
  return STATUS_CHIP_SX.leave;
};

export const isEmployeeStatus = (value: unknown): value is EmployeeStatus =>
  typeof value === 'string' && (ALL_EMPLOYEE_STATUSES as string[]).includes(value);
